import { useEffect, useState } from "react";
import useTitanApp from "@/hooks/useTitanApp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, AlertTriangle, FileText } from "lucide-react";
import { cn } from "@/lib/utils";
import { getPilUrl } from "@/lib/pharmacyFirst/utils/pilUtils";
import { httpPost } from "@/services/httpService";
import { sendAhtWeightManagementEmailUrl } from "@/apiUrls/apiUrls";

interface AHTPatientEmailModalProps {
  open: boolean;
  patientName: string;
  patientEmail: string | null;
  drugId: string;
  drugDisplayName: string;
  initialDose: string;
  nextAppointmentDate: string | null;
  isDarkMode?: boolean;
  onSend: () => void;
  onSkip: () => void;
}

const MONTHS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

function formatDate(isoDate: string): string {
  const date = new Date(isoDate + "T00:00:00Z");
  const day = date.getUTCDate();
  const month = MONTHS[date.getUTCMonth()];
  const year = date.getUTCFullYear();
  if (month === undefined) return isoDate;
  return `${day} ${month} ${year}`;
}

export function AHTPatientEmailModal({
  open,
  patientName,
  patientEmail,
  drugId,
  drugDisplayName,
  initialDose,
  nextAppointmentDate,
  isDarkMode = false,
  onSend,
  onSkip,
}: AHTPatientEmailModalProps) {
  const [isSending, setIsSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const { appState } = useTitanApp();
  const { currentPharmacy } = appState || {};
  const pharmacyName = currentPharmacy?.teamPharmacyName;
  const pharmacyId = currentPharmacy?.teamPharmacyID;

  // The leaflets live in the public S3 assets bucket, so the registry already
  // holds an absolute URL — prefixing the app origin would break the email link.
  const pilUrl = getPilUrl(drugId);
  const hasEmail = !!patientEmail;

  const subject = `Your weight management medication from ${pharmacyName || "your pharmacy"}`;

  const buildBodyHtml = () => {
    const lines = [
      `<p>Dear ${patientName},</p>`,
      `<p>Following your consultation today, you have been supplied with ${drugDisplayName} at a starting dose of ${initialDose}.</p>`,
    ];
    if (nextAppointmentDate) {
      lines.push(
        `<p>Your next review appointment is scheduled for ${formatDate(nextAppointmentDate)}.</p>`,
      );
    }
    lines.push(
      "<p>Please read the attached Patient Information Leaflet carefully before starting your medication.</p>",
    );
    if (pilUrl) {
      lines.push(`<p><a href="${pilUrl}">Patient Information Leaflet (PDF)</a></p>`);
    }
    lines.push(
      "<p>If you have any questions or concerns about your medication, please contact us.</p>",
    );
    lines.push(`<p>Kind regards, ${pharmacyName || "your pharmacy"}</p>`);
    return lines.join("\n");
  };

  useEffect(() => {
    if (!open) {
      setIsSending(false);
      setSendError(null);
    }
  }, [open]);

  const handleSend = async () => {
    setIsSending(true);
    setSendError(null);
    try {
      await httpPost(sendAhtWeightManagementEmailUrl(), {
        recipientEmail: patientEmail,
        subject,
        body: buildBodyHtml(),
        pharmacyId,
      });
      onSend();
    } catch {
      setSendError("Could not send the email. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onSkip(); }}>
      <DialogContent
        className={cn(
          "max-w-lg",
          isDarkMode ? "bg-gray-800 text-gray-100 border-gray-700" : "",
        )}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Send patient summary email
          </DialogTitle>
        </DialogHeader>

        {!hasEmail && (
          <div
            className={cn(
              "flex items-start gap-2 rounded-lg border p-3 text-sm",
              isDarkMode
                ? "border-yellow-700 bg-yellow-900/20 text-yellow-200"
                : "border-yellow-300 bg-yellow-50 text-yellow-800",
            )}
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>No email address on file — email cannot be sent. Click Skip to continue.</span>
          </div>
        )}

        {sendError && (
          <div
            className={cn(
              "flex items-start gap-2 rounded-lg border p-3 text-sm",
              isDarkMode
                ? "border-red-700 bg-red-900/20 text-red-200"
                : "border-red-300 bg-red-50 text-red-800",
            )}
          >
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>{sendError}</span>
          </div>
        )}

        <div
          className={cn(
            "rounded-lg border p-4 text-sm space-y-3",
            isDarkMode ? "border-gray-600 bg-gray-700" : "border-gray-200 bg-gray-50",
          )}
        >
          <div className={cn("pb-2 border-b", isDarkMode ? "border-gray-600" : "border-gray-200")}>
            <span className={cn("text-xs uppercase tracking-wide", isDarkMode ? "text-gray-400" : "text-gray-500")}>
              Subject
            </span>
            <p className="font-medium">{subject}</p>
          </div>

          <div className="space-y-2">
            <p>Dear {patientName},</p>
            <p>
              Following your consultation today, you have been supplied with {drugDisplayName} at a
              starting dose of {initialDose}.
            </p>
            {nextAppointmentDate && (
              <p>Your next review appointment is scheduled for {formatDate(nextAppointmentDate)}.</p>
            )}
            <p>
              Please read the attached Patient Information Leaflet carefully before starting your
              medication.
            </p>
            {pilUrl && (
              <a
                href={pilUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  "inline-flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium",
                  isDarkMode
                    ? "bg-purple-900/40 text-purple-200 hover:bg-purple-900/60"
                    : "bg-purple-100 text-purple-800 hover:bg-purple-200",
                )}
              >
                <FileText className="h-4 w-4" />
                Patient Information Leaflet (PDF)
              </a>
            )}
            <p>If you have any questions or concerns about your medication, please contact us.</p>
            <p>Kind regards, {pharmacyName || "your pharmacy"}</p>
          </div>
        </div>

        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onSkip}>
            Skip
          </Button>
          <Button onClick={handleSend} disabled={!hasEmail || isSending}>
            {isSending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              "Send email"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
